// Modules
import { useEffect, useState } from "react"
import { useForm, FormProvider } from "react-hook-form"

// Constants
import { ERROR_MESSAGES } from "~constants/errorMessages"

// Components
import Input from "~app/Input"
import Modal from "~app/Modal"
import Textarea from "~app/Textarea"
import MessageSent from "~components/MessageSent"
import { ErrorMessageSmall } from "~styled/errors"
import { WizardStepContainer } from "~styled/wizard"
import { ModalTitle, ModalText } from "~styled/modal"
import { WizardColumn, WizardForm } from "~styled/wizard"

// Shapes
import { OnRequestCloseProps } from "~shapes/modal"

interface RequestCreationModalProps {
  isLoading: boolean
  modalText: string
  onSuccess: boolean
  modalTitle: string
  errorMessage: string
  handleSave: (formData: any) => void
  onRequestClose: (payload: OnRequestCloseProps) => void
}

const RequestCreationModal = ({
  isLoading,
  modalText,
  onSuccess,
  modalTitle,
  errorMessage,
  handleSave,
  onRequestClose,
}: RequestCreationModalProps) => {
  const contactForm = useForm()

  const [modalWidth, setModalWidth] = useState<string>("500px")
  const [stepNumber, setStepNumber] = useState<number>(1)

  useEffect(() => {
    switch (stepNumber) {
      case 1:
        setModalWidth("500px")
        break

      default:
        setModalWidth("900px")
        break
    }
  }, [stepNumber])

  const onMoveStepForward = () => {
    if (onSuccess) {
      onRequestClose({ shouldRefreshList: false })
      return
    }

    switch (stepNumber) {
      case 1:
        setStepNumber(2)
        break

      case 2:
        contactForm.handleSubmit(handleSave)()
        break
    }
  }

  return (
    <Modal
      isOpen={true}
      maxWidth={modalWidth}
      buttonLabel={!onSuccess ? "Send email" : "Done"}
      cancelLabel="Cancel"
      cancelButton={!onSuccess}
      isButtonLoading={isLoading}
      onRequestClose={() => onRequestClose({ shouldRefreshList: false })}
      onRequestCancel={() => onRequestClose({ shouldRefreshList: false })}
      onRequestConfirm={onMoveStepForward}
    >
      {!onSuccess && (
        <>
          <ModalTitle>{modalTitle}</ModalTitle>

          {stepNumber === 1 && (
            <div>
              <ModalText>{modalText}</ModalText>
            </div>
          )}

          <WizardStepContainer className={stepNumber === 2 ? "visible" : ""}>
            <WizardForm>
              <FormProvider {...contactForm}>
                <WizardColumn>
                  <Input
                    name="subject"
                    label="Subject"
                    register={contactForm.register("subject", { required: true })}
                    errorMessage={
                      contactForm.formState.errors.subject &&
                      ERROR_MESSAGES.FIELD_REQUIRED
                    }
                  />

                  <Textarea
                    name="message"
                    label="Message"
                    register={contactForm.register("message", { required: true })}
                    errorMessage={
                      contactForm.formState.errors.message &&
                      ERROR_MESSAGES.FIELD_REQUIRED
                    }
                  />
                </WizardColumn>
              </FormProvider>
            </WizardForm>
          </WizardStepContainer>

          <ErrorMessageSmall style={{ textAlign: "right" }}>
            {errorMessage}
          </ErrorMessageSmall>
        </>
      )}

      {onSuccess && (
        <MessageSent
          title="Message sent"
          message="Our team will respond within 2 hours. It may take a bit longer on evening nad weekends."
        />
      )}
    </Modal>
  )
}

export default RequestCreationModal
